import { combineTransactionSteps, findChildrenInRange, // findChildrenInRange,
getChangedRanges } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { indexOfFirstInvalidHashtagCharacter, validateHashtag, validateHashtagText } from '../utils/validateHashtag';
import { getMarksOfTypeTouchedByPaddedRange } from '../utils/prosemirror';
import { hashtagInTextBlockRegex } from '../utils/hashtagRegex';
/**
 * If we added invalid content to the middle of a hashtag, unlink anything split from the `#` character
 */ function splitHashtagAtInvalidCharacter(hashtag, hashtagTextIncludingHash, tr, type) {
    if (!validateHashtag(hashtagTextIncludingHash) && hashtagTextIncludingHash.length > 1) {
        const firstInvalidIndex = indexOfFirstInvalidHashtagCharacter(hashtagTextIncludingHash.slice(1));
        if (firstInvalidIndex >= 1) {
            tr.removeMark(hashtag.from, hashtag.to, type);
            tr.addMark(hashtag.from, hashtag.from + firstInvalidIndex + 1, type.create({
                'data-hashtag': hashtagTextIncludingHash.slice(1, firstInvalidIndex + 1)
            }));
        } else {
            tr.removeMark(hashtag.from, hashtag.to, type);
        }
        return true;
    }
    return false;
}
/**
 * If, after subtractions, we are left with a stray `#` character, remove the hashtag mark from it.
 */ function unmarkStrayHashCharacters(hashtag, hashtagTextIncludingHash, tr, type) {
    if (hashtagTextIncludingHash.length === 1) {
        tr.removeMark(hashtag.from, hashtag.to, type);
        return true;
    }
    return false;
}
/**
 * Not a case by itself, just a utility.
 *
 * Takes a hashtag and the node that includes it, then determines if we should extend the hashtag's range to envelope
 * whatever comes after the existing hashtag's range.
 */ function rangeToExtendHashtag(hashtag, nodeWithHashtag) {
    const matches = [
        ...nodeWithHashtag.node.textContent.matchAll(hashtagInTextBlockRegex)
    ];
    if (!(matches === null || matches === void 0 ? void 0 : matches.length)) {
        return false;
    }
    return matches.reduce((accumulator, match)=>{
        if (match.index && match.length > 0 && match.index + 1 === hashtag.from) {
            const matchText = match[0];
            const potentialHashtagText = nodeWithHashtag.node.textBetween(hashtag.from, hashtag.from + matchText.length - 1);
            return matchText.length !== hashtag.to - hashtag.from && validateHashtagText(potentialHashtagText) ? {
                from: hashtag.from,
                to: hashtag.from + matchText.length,
                hashtagText: matchText.slice(1)
            } : false;
        }
        return accumulator;
    }, false);
}
/**
 * Another case we need to worry about is invalid characters being deleted between an existing hashtag and some
 * hashtag-valid text. We should add the now connected, unmarked text to the existing hashtag.
 */ function extendHashtagEnd(nodesInRange, hashtag, hashtagTextIncludingHash, tr, type) {
    const nodeWithHashtag = nodesInRange.find((nodeWithPos)=>{
        return nodeWithPos.node.textContent.includes(hashtagTextIncludingHash);
    });
    if (nodeWithHashtag) {
        const newHashtagRange = rangeToExtendHashtag(hashtag, nodeWithHashtag);
        if (newHashtagRange) {
            tr.removeMark(hashtag.from, hashtag.to, type);
            tr.addMark(newHashtagRange.from, newHashtagRange.to, type.create({
                'data-hashtag': newHashtagRange.hashtagText
            }));
            return true;
        }
    }
    return false;
}
/**
 * This is a catch-all to make sure the data-hashtag attribute remains updated
 */ function updateMarkAttributesWithAdditionsAndSubtractions(hashtag, hashtagTextIncludingHash, tr, type) {
    if (validateHashtag(hashtagTextIncludingHash) && hashtagTextIncludingHash.length > 1) {
        tr.removeMark(hashtag.from, hashtag.to);
        tr.addMark(hashtag.from, hashtag.to, type.create({
            'data-hashtag': hashtagTextIncludingHash.slice(1)
        }));
        return true;
    }
    return false;
}
/**
 * Runs functions from the provided array until one returns true. We use this better modularize the edge cases we need
 * to handle.
 */ function runEachCaseAndBreakOnTrue(array) {
    const result = array.find((func)=>func());
    return !!result;
}
/**
 * Handles all modifications to existing hashtags. This will never create a brand new hashtag, only alter existing ones.
 */ export function hashtagModification(options) {
    return new Plugin({
        key: new PluginKey('hashtag-mark-modification'),
        appendTransaction: (transactions, oldState, newState)=>{
            const docChanges = transactions.some((transaction)=>transaction.docChanged) && !oldState.doc.eq(newState.doc);
            if (!docChanges) {
                return;
            }
            const { tr } = newState;
            const transform = combineTransactionSteps(oldState.doc, [
                ...transactions
            ]);
            const changes = getChangedRanges(transform);
            changes.forEach(({ newRange })=>{
                const nodesInRange = findChildrenInRange(newState.doc, newRange, (node)=>node.isTextblock);
                const hashtags = getMarksOfTypeTouchedByPaddedRange(newState.doc, newRange, options.type);
                if (hashtags.length) {
                    hashtags.forEach((hashtag)=>{
                        const hashtagTextIncludingHash = newState.doc.textBetween(hashtag.from, hashtag.to);
                        runEachCaseAndBreakOnTrue([
                            ()=>unmarkStrayHashCharacters(hashtag, hashtagTextIncludingHash, tr, options.type),
                            ()=>splitHashtagAtInvalidCharacter(hashtag, hashtagTextIncludingHash, tr, options.type),
                            ()=>extendHashtagEnd(nodesInRange, hashtag, hashtagTextIncludingHash, tr, options.type),
                            ()=>updateMarkAttributesWithAdditionsAndSubtractions(hashtag, hashtagTextIncludingHash, tr, options.type)
                        ]);
                    });
                }
            });
            if (!tr.steps.length) {
                return;
            }
            return tr;
        }
    });
}

//# sourceMappingURL=hashtagModification.js.map